import { forwardRef, useId } from "react";

import { cn } from "./cn";

/**
 * Controles de formulario con label asociado (design system §6.3 + §10).
 *
 * Cierra el gap de accesibilidad #1 del admin: antes había 301 inputs y
 * sólo 2 usaban `htmlFor`. Acá el label se asocia SIEMPRE por `id`
 * (autogenerado con `useId` si no se pasa), el error se expone con
 * `aria-invalid` + `aria-describedby`, y el estilo de foco/error vive en
 * un solo lugar.
 *
 * Soporta `ref` (para `react-hook-form` `register`) en los tres controles.
 *
 * `required` dibuja el asterisco y llega al control como `aria-required`
 * (auditoría A11Y-01): el lector de pantalla lo anuncia. No se pasa como
 * `required` nativo para no disparar los globos del navegador por encima de
 * la validación propia de cada formulario.
 */

/** Clases del control desnudo. Escape hatch para casos que arman su propio
 *  layout de label (evitar salvo necesidad). */
export const controlClasses =
  "w-full rounded-md border border-neutral-450 bg-white px-3 py-1.5 text-sm shadow-sm " +
  "placeholder:text-neutral-500 focus:border-brand-500 focus:outline-none focus:ring-1 " +
  "focus:ring-brand-500 disabled:bg-neutral-50 disabled:opacity-60";

const CONTROL_ERROR =
  "border-rose-500 focus:border-rose-600 focus:ring-rose-500";

interface FieldBaseProps {
  label?: string | undefined;
  /** Ayuda gris debajo del control (regla no obvia). */
  hint?: string | undefined;
  /** Mensaje de error; pinta el control en rose y setea `aria-invalid`. */
  error?: string | undefined;
  required?: boolean | undefined;
  /** Clase del contenedor (para grid spans, etc.). */
  containerClassName?: string | undefined;
}

interface FieldShellProps extends FieldBaseProps {
  id: string;
  descId: string | undefined;
  children: React.ReactNode;
}

function FieldShell({
  id,
  label,
  hint,
  error,
  required,
  containerClassName,
  descId,
  children,
}: FieldShellProps): JSX.Element {
  return (
    <div className={cn("space-y-1", containerClassName)}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-neutral-700"
        >
          {label}
          {required && (
            <span className="text-rose-600" aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </label>
      )}
      {children}
      {(error ?? hint) && (
        <p
          id={descId}
          className={cn("text-xs", error ? "text-rose-700" : "text-neutral-600")}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "id"> &
  FieldBaseProps & { id?: string | undefined };

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, containerClassName, id, className, ...rest },
  ref,
): JSX.Element {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const descId = error ?? hint ? `${fieldId}-desc` : undefined;
  return (
    <FieldShell
      id={fieldId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      containerClassName={containerClassName}
      descId={descId}
    >
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-required={required ? true : undefined}
        aria-describedby={descId}
        className={cn(controlClasses, error && CONTROL_ERROR, className)}
        {...rest}
      />
    </FieldShell>
  );
});

type TextareaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "id"
> &
  FieldBaseProps & { id?: string | undefined };

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    {
      label,
      hint,
      error,
      required,
      containerClassName,
      id,
      className,
      rows = 3,
      ...rest
    },
    ref,
  ): JSX.Element {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const descId = error ?? hint ? `${fieldId}-desc` : undefined;
    return (
      <FieldShell
        id={fieldId}
        label={label}
        hint={hint}
        error={error}
        required={required}
        containerClassName={containerClassName}
        descId={descId}
      >
        <textarea
          ref={ref}
          id={fieldId}
          rows={rows}
          aria-invalid={error ? true : undefined}
          aria-required={required ? true : undefined}
          aria-describedby={descId}
          className={cn(controlClasses, error && CONTROL_ERROR, className)}
          {...rest}
        />
      </FieldShell>
    );
  },
);

type SelectProps = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "id"> &
  FieldBaseProps & { id?: string | undefined };

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    required,
    containerClassName,
    id,
    className,
    children,
    ...rest
  },
  ref,
): JSX.Element {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const descId = error ?? hint ? `${fieldId}-desc` : undefined;
  return (
    <FieldShell
      id={fieldId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      containerClassName={containerClassName}
      descId={descId}
    >
      <select
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-required={required ? true : undefined}
        aria-describedby={descId}
        className={cn(controlClasses, error && CONTROL_ERROR, className)}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  );
});
